// ui/cell-name-snap.js: C5 whole-pixel snap for empty-cell seat names.
//
// Each empty trick cell centres its seat's name (.cell-name, plus a
// .pass line for a seat that passed) with flexbox. Centring lands the
// name half a pixel off whenever the cell and the name differ in
// odd/even, and the C5 whole-pixel check then fails ("BOT ATOM" at
// 407.5,409.5 at 1024x768). The snap measures each rendered name once
// after it changes and shifts it by at most half a pixel, so its box
// starts on a whole CSS pixel while staying centred to within half a
// pixel. The shift rides on CSS translate, which moves the words without
// touching the flex layout, the text, or the cell boxes.

// Within this of a whole pixel counts as on it (the C5 check reads to
// 0.01 px; box reads carry float noise well under that).
const ON_PIXEL = 0.01;

/**
 * How far `value` (a CSS px box edge) must move to sit on a whole
 * pixel. Always within half a pixel; 0 when already whole. Pure.
 *
 * @param {number} value
 * @returns {number}
 */
export function snapDelta(value) {
  if (!Number.isFinite(value)) {
    throw new Error(`snapDelta: expected a finite number, got ${value}`);
  }
  const shift = Math.round(value) - value;
  return Math.abs(shift) < ON_PIXEL ? 0 : shift;
}

/**
 * The translate that puts a box at (`left`, `top`) onto whole CSS
 * pixels. Pure.
 *
 * @param {number} left
 * @param {number} top
 * @returns {{ dx: number, dy: number }}
 */
export function snapShifts(left, top) {
  return { dx: snapDelta(left), dy: snapDelta(top) };
}

// "x" or "x y" in px (a browser drops a zero y when it reads the
// translate back); null for any other form.
function parseShift(text) {
  if (text === undefined || text === null || text === '' || text === 'none') return [0, 0];
  const parts = String(text).trim().split(/\s+/);
  if (parts.length > 2 || !parts.every((p) => /^-?[\d.]+(e-?\d+)?px$/.test(p))) return null;
  const [x, y = 0] = parts.map(parseFloat);
  return [x, y];
}

/**
 * Snap every visible seat name under `root` onto whole CSS pixels.
 * Each pass first clears its own old shift, then reads each name's box
 * once and writes the missing half-pixel (at most) to its CSS
 * translate, so a name that is already whole is left alone, a second
 * pass is a no-op, and a name whose width changed since the last pass
 * is measured fresh instead of piling a new shift onto the old one.
 * Call after the names render or change (text, cell size, font load,
 * the system-font switch), never per frame.
 *
 * @param {ParentNode} root
 * @returns {number} how many names were shifted
 */
export function alignCellNames(root) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    throw new Error('alignCellNames: root is missing');
  }
  const items = [...root.querySelectorAll('.cell-label .cell-name, .cell-label .pass')]
    .map((el) => /** @type {HTMLElement} */ (el));
  for (const item of items) {
    const cur = parseShift(item.style.translate);
    if (!cur) {
      throw new Error(`alignCellNames: cannot read the translate of "${(item.textContent || '').trim().slice(0, 18)}"`);
    }
    if (cur[0] || cur[1]) item.style.translate = '';
  }
  let moved = 0;
  for (const item of items) {
    const box = item.getBoundingClientRect();
    if (!box.width || !box.height) continue;
    const { dx, dy } = snapShifts(box.left, box.top);
    if (!dx && !dy) continue;
    item.style.translate = `${dx}px ${dy}px`;
    moved += 1;
  }
  return moved;
}
